import React, {useState} from 'react';
import type {InjectedRouter} from 'react-router';

import {resource} from '@albert-io/json-api-framework/request/builder';
import {addToast, IconButton, Menu, copyToClipboard} from '@albert-io/atomic';
import {normalizeError} from 'lib/normalizeError';
import ArchivedQuestionModal from 'client/components/ArchivedQuestions/ArchivedQuestionModal.react';
import {getArchivedQuestionCount} from 'client/components/ArchivedQuestions/useArchivedQuestionModal';

import type {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';

import {AssignmentActionModals} from './AssignmentActionModals';
import type {AssignmentModalType} from './AssignmentActionModals';
import {getEditPath, getShareUrl} from './AssignmentMenu.helpers';

// Menu.Item fires onClick on both click and Enter/Space, so one handler covers keyboard too.
type ActivateHandler = (e?: React.MouseEvent | React.KeyboardEvent) => void;

// "Document Details" shows the creator's name, which the lean GQL row lacks (only teacherId).
// !todo: drop this fetch once BE adds the teacher (creator) name to the Assignment GQL type.
const fetchCreatorName = async (assignmentId: string, isDraft: boolean): Promise<string | null> => {
  const assignment = (await resource(isDraft ? 'draft_assignment_v1' : 'assignment_v3')
    .mandarkEndpoint([isDraft ? 'draft_assignments_v1' : 'assignments_v3', assignmentId])
    .include('teacher_v1')
    .getResourcePromise()) as AssignmentModelV3;

  return assignment.getTeacher()?.getFullName() ?? null;
};

interface FolderQuestionInfo {
  questionCount: number;
  archivedQuestionCount: number;
}

// The lean GQL row can't see archived questions, so fetch the assignment's sets to count them — the
// shared HeaderElements menu does this from a Mandark model the GQL list doesn't carry.
const fetchFolderQuestionInfo = async (
  assignmentId: string,
  isDraft: boolean
): Promise<FolderQuestionInfo> => {
  const assignment = (await resource(isDraft ? 'draft_assignment_v1' : 'assignment_v3')
    .mandarkEndpoint([isDraft ? 'draft_assignments_v1' : 'assignments_v3', assignmentId])
    .include('question_sets_v1.questions_v3')
    .getResourcePromise()) as AssignmentModelV3;

  const questionSets = assignment.getQuestionSets();
  const questionCount = questionSets.reduce((total, set) => total + set.getQuestions().size, 0);

  return {questionCount, archivedQuestionCount: getArchivedQuestionCount(questionSets)};
};

interface AssignmentMenuOptionsProps {
  assignmentId: string;
  assignmentName: string;
  isDraft: boolean;
  canEdit: boolean;
  /** Class-scoped list → Delete unassigns only this class, and View Student Answers is enabled. */
  classroomId?: string;
  /** Report path for this row; absent (e.g. no acting teacher) hides View Report. */
  reportPath?: string | null;
  /** ISO timestamp for the Document Details "Last modified" line. */
  updatedAt: string;
  router: InjectedRouter;
  onDeleteComplete: () => void;
}

export const AssignmentMenuOptions = ({
  assignmentId,
  assignmentName,
  isDraft,
  canEdit,
  classroomId,
  reportPath,
  updatedAt,
  router,
  onDeleteComplete
}: AssignmentMenuOptionsProps) => {
  const [modal, setModal] = useState<AssignmentModalType>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [creatorName, setCreatorName] = useState<string | null>(null);
  // A fetch can legitimately resolve null (no teacher) — null must not read as "not yet fetched".
  const [creatorFetched, setCreatorFetched] = useState(false);
  const [folderInfo, setFolderInfo] = useState<FolderQuestionInfo | null>(null);
  const [isPreparingFolder, setIsPreparingFolder] = useState(false);

  const isClassScoped = Boolean(classroomId);
  const editPath = getEditPath(assignmentId, isDraft);

  const openModal =
    (type: AssignmentModalType): ActivateHandler =>
    (e) => {
      e?.stopPropagation();
      setModal(type);
    };

  const goToEdit: ActivateHandler = (e) => {
    e?.stopPropagation();
    router.push(editPath);
  };

  const goToReport: ActivateHandler = (e) => {
    e?.stopPropagation();
    if (reportPath) {
      router.push(reportPath);
    }
  };

  const openStudentAnswers: ActivateHandler = (e) => {
    e?.stopPropagation();
    if (!classroomId) {
      return;
    }
    // The submissions index resolves the class's first student and redirects into their view.
    router.push(`/classes/${classroomId}/assignments/${assignmentId}/student-detail`);
  };

  const openDetails: ActivateHandler = async (e) => {
    e?.stopPropagation();
    if (isLoadingDetails) {
      return;
    }
    if (!creatorFetched) {
      setIsLoadingDetails(true);
      try {
        setCreatorName(await fetchCreatorName(assignmentId, isDraft));
        setCreatorFetched(true);
      } catch (error) {
        addToast({
          color: 'negative',
          title: "Couldn't load details",
          message: normalizeError(error).message
        });
        return;
      } finally {
        setIsLoadingDetails(false);
      }
    }
    setModal('details');
  };

  const openFolderModal: ActivateHandler = async (e) => {
    e?.stopPropagation();
    if (isPreparingFolder) {
      return;
    }
    setIsPreparingFolder(true);
    try {
      const info = await fetchFolderQuestionInfo(assignmentId, isDraft);
      setFolderInfo(info);
      // Warn first when archived questions exist — they're excluded from what gets saved.
      setModal(info.archivedQuestionCount > 0 ? 'archived' : 'folder');
    } catch (error) {
      addToast({
        color: 'negative',
        title: "Couldn't open Save to Folder",
        message: normalizeError(error).message
      });
    } finally {
      setIsPreparingFolder(false);
    }
  };

  const copyShareLink =
    (type: 'student' | 'educator'): ActivateHandler =>
    (e) => {
      e?.stopPropagation();
      copyToClipboard(getShareUrl(type, assignmentId), () =>
        addToast({
          color: 'positive',
          title: 'Link Copied!',
          message: `The ${type} link to this assignment has been copied to your clipboard.`
        })
      );
    };

  // Divider-separated groups of Menu.Items; Menu closes itself on item click (closeOnItemClick).
  const groups: {key: string; items: React.ReactNode[]}[] = [
    {
      key: 'edit',
      items: [
        <Menu.Item
          key='edit'
          leftIconProps={{icon: isDraft ? 'pencil' : 'gear'}}
          onClick={goToEdit}
        >
          {isDraft ? 'Edit Draft' : 'Edit Recipient Settings'}
        </Menu.Item>
      ]
    },
    {
      key: 'reports',
      items: isDraft
        ? []
        : [
            reportPath ? (
              <Menu.Item key='report' leftIconProps={{icon: 'chart-line'}} onClick={goToReport}>
                View Report
              </Menu.Item>
            ) : null,
            // !todo Gated to a single classroom for now; the FRQ teacher-grading work may lift this
            // boundary so submissions can be viewed across classes.
            // Cross-class: keep it non-actionable + aria-disabled, but render it readable per DQA.
            // `readonly` blocks the click like `disabled` without the atomic's faded icon/opacity;
            // we just add the not-allowed cursor + content-tertiary color on top.
            <Menu.Item
              key='answers'
              leftIconProps={{icon: 'file-lines', color: isClassScoped ? undefined : 'tertiary'}}
              textProps={isClassScoped ? undefined : {color: 'tertiary'}}
              className={isClassScoped ? undefined : 'pointer-events-auto! cursor-not-allowed!'}
              aria-disabled={isClassScoped ? undefined : true}
              disableHover={!isClassScoped}
              onClick={openStudentAnswers}
              readonly={!isClassScoped}
              subLabel={isClassScoped ? undefined : 'Enabled when viewing an individual classroom.'}
            >
              View Student Answers
            </Menu.Item>
          ]
    },
    {
      key: 'manage',
      items: isDraft
        ? []
        : [
            <Menu.Item
              key='reassign'
              leftIconProps={{icon: 'rotate-right'}}
              onClick={openModal('reassign')}
            >
              Duplicate & Reassign
            </Menu.Item>,
            <Menu.Item
              key='folder'
              leftIconProps={{icon: 'folder'}}
              onClick={openFolderModal}
              disabled={isPreparingFolder}
            >
              Save Questions to Folder
            </Menu.Item>
          ]
    },
    {
      key: 'share',
      items: isDraft
        ? []
        : [
            <Menu.Item
              key='student-share'
              leftIconProps={{icon: 'copy'}}
              onClick={copyShareLink('student')}
            >
              Student Share Link
            </Menu.Item>,
            <Menu.Item
              key='educator-share'
              leftIconProps={{icon: 'copy'}}
              onClick={copyShareLink('educator')}
            >
              Educator Share Link
            </Menu.Item>
          ]
    },
    {
      key: 'meta',
      items: [
        <Menu.Item
          key='details'
          leftIconProps={{icon: 'memo-circle-info'}}
          onClick={openDetails}
          disabled={isLoadingDetails}
        >
          Document Details
        </Menu.Item>,
        <Menu.Item
          key='delete'
          leftIconProps={{icon: 'trash-can', color: 'negative'}}
          textProps={{color: 'negative'}}
          onClick={openModal('delete')}
          disabled={!canEdit}
          subLabel={canEdit ? undefined : "You don't have permission to delete this assignment."}
          data-testid={`delete-${assignmentId}`}
        >
          Delete
        </Menu.Item>
      ]
    }
  ];

  const visibleGroups = groups
    .map((group) => ({key: group.key, items: group.items.filter(Boolean)}))
    .filter((group) => group.items.length > 0);

  return (
    <>
      <Menu closeOnItemClick position='bottom-end' positionFixed>
        <Menu.Trigger>
          {(triggerProps) => (
            <IconButton
              {...triggerProps}
              icon='ellipsis-v'
              label='menu'
              tooltip='More options'
              data-testid={`actions-${assignmentId}`}
            />
          )}
        </Menu.Trigger>
        <Menu.Content className='min-w-[245px]'>
          {visibleGroups.map((group, index) => (
            <React.Fragment key={group.key}>
              {index > 0 && <Menu.Divider />}
              {group.items}
            </React.Fragment>
          ))}
        </Menu.Content>
      </Menu>

      <ArchivedQuestionModal
        isOpen={modal === 'archived'}
        archivedQuestionCount={folderInfo?.archivedQuestionCount ?? 0}
        type='save-to-folder'
        onConfirm={() => setModal('folder')}
        onCancel={() => setModal(null)}
      />

      <AssignmentActionModals
        modal={modal}
        onClose={() => setModal(null)}
        assignmentId={assignmentId}
        assignmentName={assignmentName}
        isDraft={isDraft}
        classroomId={classroomId}
        creatorName={creatorName}
        updatedAt={updatedAt}
        questionCount={folderInfo?.questionCount ?? 0}
        archivedQuestionCount={folderInfo?.archivedQuestionCount ?? 0}
        onDeleted={onDeleteComplete}
      />
    </>
  );
};
